import {
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "@/lib/bap/identity-backup";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import {
	arrayBufferToBase64,
	base64ToArrayBuffer,
	generatePRFSalt,
} from "./encryption";
import {
	authenticateWithPRF,
	cancelPRFAuthentication,
	generateChallenge,
	toClientCredential,
} from "./prf";
import {
	ciphertextHash,
	fetchPRFCatalog,
	type PRFStorage,
	readPRFCatalog,
	storePRFCatalog,
} from "./prf-server-persistence";
import {
	assertPrfSession as assertSession,
	prfSessionGeneration,
} from "./prf-session";
import {
	credentialContext,
	deriveVaultKey,
	openVaultData,
	sealVaultData,
	vaultKeyFromBytes,
} from "./prf-vault-encryption";
export function clearSessionPrfKey(): void {
	cancelPRFAuthentication();
}
function snapshot(storage: PRFStorage = localStorage) {
	return {
		generation: prfSessionGeneration(),
		pending: storage.getItem(STORAGE_KEYS.PRF_VAULT_PENDING),
		portable: storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP),
		decrypted: sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP),
	};
}
function assertSnapshot(
	before: ReturnType<typeof snapshot>,
	storage: PRFStorage = localStorage
) {
	assertSession(before.generation);
	if (
		storage.getItem(STORAGE_KEYS.PRF_VAULT_PENDING) !== before.pending ||
		storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== before.portable ||
		sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) !== before.decrypted
	) {
		throw new Error(
			"Your backup changed while this request was running. Try again."
		);
	}
}
const AUTH_SUBDOMAIN_RE = /^auth\./;
const rpId = () => window.location.hostname.replace(AUTH_SUBDOMAIN_RE, "");

/** No secret is persisted: the PRF wraps a domain-separated backup key. */
export async function setupPRFEncryption(
	credentialId: string,
	value: unknown
): Promise<void> {
	const backup = validateIdentityBackup(value);
	if (isSeedIdentityBackup(backup) && backup.inventoryComplete === false) {
		throw new Error(
			"Restore your complete backup before enabling passkey unlock."
		);
	}
	const guard = snapshot();

	const catalog = await fetchPRFCatalog();
	const existing = catalog.credentials.find(
		(row) => row.credentialId === credentialId
	);
	const prfSalt = existing?.prfSalt ?? arrayBufferToBase64(generatePRFSalt());
	const vaultKey = await deriveVaultKey(backup);
	if (catalog.vault && catalog.vault.keyId !== vaultKey.keyId) {
		throw new Error(
			"This account has a different backup. Restore its current backup before enabling this passkey."
		);
	}
	assertSnapshot(guard);
	const result = await authenticateWithPRF({
		credential: toClientCredential({ credentialId, prfSalt }),
		challenge: generateChallenge(),
		rpId: rpId(),
	});
	if (!(result.success && result.encryptionKey)) {
		throw new Error(result.error ?? "Passkey unlock wasn't enabled.");
	}
	const encryptedBackup = await sealVaultData(
		arrayBufferToBase64(vaultKey.bytes),
		result.encryptionKey,
		vaultKey.keyId,
		credentialContext(credentialId, prfSalt)
	);
	const vaultEncryptedBackup = await sealVaultData(
		JSON.stringify(backup),
		vaultKey.key,
		vaultKey.keyId,
		"backup"
	);
	assertSnapshot(guard);
	// Preserve edits made while the first vault is being created on the server.
	if (!catalog.vault) {
		const targetHash = await ciphertextHash(JSON.stringify(backup));
		assertSnapshot(guard);
		if (!guard.pending) {
			localStorage.setItem(
				STORAGE_KEYS.PRF_VAULT_PENDING,
				JSON.stringify({
					keyId: vaultKey.keyId,
					baseHash: targetHash,
					targetHash,
					encryptedBackup: vaultEncryptedBackup,
				})
			);
			guard.pending = localStorage.getItem(STORAGE_KEYS.PRF_VAULT_PENDING);
		}
	}

	const res = await fetch("/api/webauthn/prf/enroll", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			credentialId,
			prfSalt,
			keyId: vaultKey.keyId,
			encryptedBackup,
			vaultEncryptedBackup,
		}),
	});
	if (!res.ok) {
		throw new Error(
			"Backup unlock wasn't saved. Your sign-in passkey still works; try enabling backup unlock again."
		);
	}
	const updated = await fetchPRFCatalog();
	assertSnapshot(guard);
	storePRFCatalog(updated);
	if (
		!catalog.vault &&
		pendingBackup()?.targetHash ===
			(await ciphertextHash(JSON.stringify(backup)))
	) {
		assertSnapshot(guard);
		localStorage.removeItem(STORAGE_KEYS.PRF_VAULT_PENDING);
	}
}

/** Fetch current ciphertext before a user-initiated unlock. Never open old PRF slots. */
export async function unlockPRFBackup(
	storage: PRFStorage = localStorage
): Promise<string | null> {
	const guard = snapshot(storage);
	const catalog = await fetchPRFCatalog();
	assertSnapshot(guard, storage);
	storePRFCatalog(catalog, storage);
	if (!catalog?.vault) {
		throw new Error(
			"Use your backup password, then enable backup unlock in Settings."
		);
	}
	const candidates = catalog.credentials.filter(
		(row) => row.keyId === catalog.vault?.keyId && row.encryptedBackup
	);
	if (!candidates.length) {
		throw new Error("Use your backup password or restore a backup.");
	}
	const captured = JSON.stringify(catalog);
	const result = await authenticateWithPRF({
		credentials: candidates.map(({ credentialId, prfSalt }) =>
			toClientCredential({ credentialId, prfSalt })
		),
		challenge: generateChallenge(),
		rpId: rpId(),
	});
	if (result.cancelled) {
		return null;
	}
	assertSnapshot(guard, storage);
	if (!(result.success && result.encryptionKey)) {
		throw new Error(
			result.error ?? "Use your backup password or try another passkey."
		);
	}
	const selected = candidates.find(
		(row) => row.credentialId === result.credentialId
	);
	if (!selected) {
		throw new Error("That passkey isn't enabled for this backup.");
	}
	const raw = await openVaultData(
		selected.encryptedBackup,
		result.encryptionKey,
		selected.keyId,
		credentialContext(selected.credentialId, selected.prfSalt)
	);
	const vaultKey = await vaultKeyFromBytes(base64ToArrayBuffer(raw));
	if (vaultKey.keyId !== selected.keyId) {
		throw new Error("Backup unlock key did not match.");
	}
	const pending = pendingBackup(storage);
	const plaintext = await openVaultData(
		pending?.keyId === vaultKey.keyId && pending.encryptedBackup
			? pending.encryptedBackup
			: catalog.vault.encryptedBackup,
		vaultKey.key,
		vaultKey.keyId,
		"backup"
	);
	validateIdentityBackup(JSON.parse(plaintext));
	assertSnapshot(guard, storage);
	if (JSON.stringify(readPRFCatalog(storage)) !== captured) {
		throw new Error(
			"Your backup changed. Unlock it again to use the latest version."
		);
	}
	return plaintext;
}

interface PendingVaultBackup {
	encryptedBackup?: string;
	keyId: string;
	baseHash: string | null;
	targetHash: string;
}
function pendingBackup(
	storage: PRFStorage = localStorage
): PendingVaultBackup | null {
	try {
		return JSON.parse(
			storage.getItem(STORAGE_KEYS.PRF_VAULT_PENDING) ?? "null"
		);
	} catch {
		return null;
	}
}
/** Preserve an explicit file restore even if its inventory differs from the cloud. */
export async function preserveImportedVaultBackup(
	value: unknown
): Promise<void> {
	const guard = snapshot();
	const key = await deriveVaultKey(value);
	const targetHash = await ciphertextHash(JSON.stringify(value));
	const encryptedBackup = await sealVaultData(
		JSON.stringify(value),
		key.key,
		key.keyId,
		"backup"
	);
	assertSnapshot(guard);
	localStorage.setItem(
		STORAGE_KEYS.PRF_VAULT_PENDING,
		JSON.stringify({
			keyId: key.keyId,
			baseHash: null,
			targetHash,
			encryptedBackup,
		})
	);
}

/** Password unlock can read the same current backup, without a PRF ceremony. */
export async function currentVaultBackup(
	value: unknown
): Promise<ReturnType<typeof validateIdentityBackup>> {
	const backup = validateIdentityBackup(value);
	const guard = snapshot();
	let catalog: Awaited<ReturnType<typeof fetchPRFCatalog>>;
	try {
		catalog = await fetchPRFCatalog();
	} catch {
		assertSnapshot(guard);
		return backup;
	}
	const key = await deriveVaultKey(backup);
	if (!catalog.vault || catalog.vault.keyId !== key.keyId) {
		assertSnapshot(guard);
		return backup;
	}
	const current = validateIdentityBackup(
		JSON.parse(
			await openVaultData(
				catalog.vault.encryptedBackup,
				key.key,
				key.keyId,
				"backup"
			)
		)
	);
	assertSnapshot(guard);
	const pending = pendingBackup();
	assertSnapshot(guard);
	storePRFCatalog(catalog);
	if (pending?.keyId === key.keyId && pending.encryptedBackup) {
		const local = validateIdentityBackup(
			JSON.parse(
				await openVaultData(
					pending.encryptedBackup,
					key.key,
					key.keyId,
					"backup"
				)
			)
		);
		assertSnapshot(guard);
		if (JSON.stringify(current) === JSON.stringify(local)) {
			localStorage.removeItem(STORAGE_KEYS.PRF_VAULT_PENDING);
		}
		return local;
	}
	return current;
}

/** Shared ciphertext changes; every credential's wrapped key remains valid. */
export async function resealPrfBackup(
	value: unknown,
	options: { prompt: boolean; previousBackup?: unknown }
): Promise<boolean> {
	const backup = validateIdentityBackup(value);
	if (!(readPRFCatalog()?.vault || pendingBackup())) {
		return false;
	}
	if (isSeedIdentityBackup(backup) && backup.inventoryComplete === false) {
		return false;
	}
	const guard = snapshot();
	const key = await deriveVaultKey(backup);
	const previousHash =
		options.previousBackup === undefined
			? null
			: await ciphertextHash(JSON.stringify(options.previousBackup));
	const targetHash = await ciphertextHash(JSON.stringify(backup));
	const priorPending = pendingBackup();
	const baseHash =
		priorPending?.keyId === key.keyId &&
		priorPending.targetHash === previousHash
			? priorPending.baseHash
			: previousHash;
	const encryptedBackup = await sealVaultData(
		JSON.stringify(backup),
		key.key,
		key.keyId,
		"backup"
	);
	assertSnapshot(guard);
	localStorage.setItem(
		STORAGE_KEYS.PRF_VAULT_PENDING,
		JSON.stringify({ keyId: key.keyId, baseHash, targetHash, encryptedBackup })
	);
	guard.pending = localStorage.getItem(STORAGE_KEYS.PRF_VAULT_PENDING);
	const catalog = await fetchPRFCatalog();
	assertSnapshot(guard);
	if (!catalog.vault) {
		return false;
	}
	if (key.keyId !== catalog.vault.keyId) {
		return false;
	}
	const current = await openVaultData(
		catalog.vault.encryptedBackup,
		key.key,
		key.keyId,
		"backup"
	);
	if (current === JSON.stringify(backup)) {
		assertSnapshot(guard);
		storePRFCatalog(catalog);
		localStorage.removeItem(STORAGE_KEYS.PRF_VAULT_PENDING);
		return true;
	}
	// Compare plaintext to the caller's pre-edit snapshot before authorizing a write.
	// This also prevents a stale device from silently publishing an older inventory.
	if (baseHash === null || (await ciphertextHash(current)) !== baseHash) {
		throw new Error(
			"Your backup changed on another device. Unlock the latest backup before saving again."
		);
	}

	const previousEncryptedBackupHash = await ciphertextHash(
		catalog.vault.encryptedBackup
	);
	assertSnapshot(guard);
	const res = await fetch("/api/webauthn/prf/vault", {
		method: "PUT",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			keyId: key.keyId,
			encryptedBackup,
			previousEncryptedBackupHash,
		}),
	});
	if (!res.ok) {
		throw new Error(
			"Backup sync did not finish. Your password-encrypted backup is saved; retry after loading the latest backup."
		);
	}
	assertSnapshot(guard);
	storePRFCatalog({ ...catalog, vault: { keyId: key.keyId, encryptedBackup } });
	localStorage.removeItem(STORAGE_KEYS.PRF_VAULT_PENDING);
	return true;
}
